import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import { IDashboardWidget } from './dashboard-widget.interface';
import { IDashboardState } from './dashboard.reducer';
import { dashboardQuery } from './dashboard.selectors';
import * as DashboardAction from './dashboard.action';
import { Actions, ofType } from '@ngrx/effects';
import { combineLatest, Observable } from 'rxjs';
import { AuthFacade } from '@amp/auth';
import { map } from 'rxjs/operators';
import { DashboardWidgetEnum, DashboardWidgetsPermissions } from '../dashboard-widgets';

@Injectable({ providedIn: 'root' })
export class DashboardFacade {
  /** store observables */
  widgets$ = this.store.pipe(select(dashboardQuery.getDashoardWidgets));
  initiated$ = this.store.pipe(select(dashboardQuery.isDashboardInitiated));
  pending$ = this.store.pipe(select(dashboardQuery.isDashboardPending));

  /** Widgets authorized to the current user based on its permissions **/
  authorizedWidgets$: Observable<DashboardWidgetEnum[]>;

  /** Widgets currently visible on the dashboard **/
  selectedWidgets$: Observable<DashboardWidgetEnum[]>;

  /** actions listeners */
  onDashboardWidgetSettingsListUpdateSuccess$ = this.actions.pipe(ofType(DashboardAction.DashboardWidgetSettingsListUpdateSuccess));

  constructor(private store: Store<IDashboardState>, private actions: Actions, authFacade: AuthFacade) {
    this.authorizedWidgets$ = authFacade.authenticatedUserPermissions$.pipe(
      map((userPermissions) => (Object.keys(DashboardWidgetEnum) as DashboardWidgetEnum[]).filter((widget) => this.userHasPermission(DashboardWidgetsPermissions.get(widget), userPermissions)))
    );
    this.selectedWidgets$ = combineLatest([this.authorizedWidgets$, this.widgets$]).pipe(
      map(([authorizedWidgets, widgetSettings]) => authorizedWidgets.filter((widget) => widgetSettings.find((w) => w.name === widget)?.settings.visible !== false))
    );
  }

  /** Inits the dashboard */
  public initDashboard() {
    this.store.dispatch(DashboardAction.DashboardInit());
  }

  /** Updates the settings of a widget */
  public updateWidgetSettings(name: string, visible: boolean) {
    this.store.dispatch(
      DashboardAction.DashboardWidgetSettingsUpdate({
        widgetSettings: {
          name,
          settings: {
            visible,
          },
        },
      })
    );
  }

  /** Updates the settings of several widgets */
  public updateWidgetListSettings(settings: IDashboardWidget[]) {
    this.store.dispatch(DashboardAction.DashboardWidgetSettingsListUpdate({ widgetsSettings: settings }));
  }

  /**
   * Checks if the user has the permissions to access the widget
   *
   * @ignore
   */
  private userHasPermission(widgetPermissions: string[], userPermissions: string[]) {
    if (widgetPermissions === undefined) {
      return true;
    }
    return widgetPermissions.filter((widgetPermission) => userPermissions.includes(widgetPermission)).length > 0;
  }
}
